/**
 * creative_studio.js — Canvas (Creative Portfolio / Lookbook)
 * ─────────────────────────────────────────────────────────────────────────────
 * Design Bible:
 *   Brand Name:   Canvas
 *   Purpose:      Creative Portfolio & Visual Branding
 *   Reads Like:   Design Lookbook / Editorial Magazine
 *   Emotion:      Expressive, Minimal, Bold
 *   Personality:  Expressive
 *   Reading Speed: Slow, scanning-first
 *   Scanning:     Left Sidebar → Right column (asymmetric visual scan)
 *   Hero Moment:  Full-bleed Solid Indigo Sidebar + Timeline Connectors & Card Grids.
 *                 The sidebar occupies 230px, colored in Indigo (#4F46E5), and bleeds
 *                 completely to the left, top, and bottom edges of the page.
 *   ATS Target:   Moderate ATS Compatibility
 *
 * Forbidden Elements:
 *   ✗ ATS-style divider rules or bottom border lines
 *   ✗ Dense tables or financial-style grids
 *   ✗ Traditional resume hierarchy (requires off-center visual flow)
 *   ✗ Monospace fonts
 */

export const creative_studio = {
  id: 'creative-studio',
  version: 3,
  renderer: 'enterprise',
  name: 'Canvas',
  layout: 'sidebar-left',
  theme: 'classic-navy',
  density: 'editorial',
  sectionDividerStyle: 'none',
  // Section ordering priorities
  sectionOrder: ['contact-sidebar', 'skills', 'education', 'languages', 'certifications', 'personal-info', 'summary', 'projects', 'experience'],
  components: {
    header: 'header-canvas-main', // Custom header rendering ONLY name & role on the right
    experience: 'experience-timeline',
    projects: 'projects-cards',
    education: 'education-sidebar',
  },
  /**
   * sectionSettings — per-section renderer style and density overrides.
   *
   * style   : overrides the registry's default rendererComponent for this section.
   *           'inline' renders a compact dot-separated single line (via InlineListBlock).
   *           Any SectionRegistry key also accepted (e.g. 'skills-sidebar', 'experience-timeline').
   *
   * density : overrides the template-level density for this section only.
   *           Injects scoped CSS variables on the SectionWrapper so only that section
   *           is affected — no global theme mutation.
   *           Values: 'compact' | 'comfortable' | 'luxury' | 'editorial'
   */
  sectionSettings: {
    // ── Sidebar sections (compact — conserve vertical space) ──
    skills:         { style: 'inline',             density: 'compact' },
    languages:      { style: 'inline',             density: 'compact' },
    certifications: { style: 'inline',             density: 'compact' },
    education:      { style: 'education-sidebar',  density: 'compact' },

    // ── Main column sections ──
    summary:    { density: 'comfortable' },
    projects:   { style: 'projects-cards',    density: 'comfortable' },
    experience: { style: 'experience-timeline', density: 'comfortable' },
  },
  layouts: {
    'sidebar-left': {
      component: 'sidebar-left',
      slots: {
        header: [], // Empty header slot to let the sidebar bleed to the absolute top of the page
        // Sidebar holds the contact details, skills, education, languages, and credentials in white text
        sidebar: ['contact-sidebar', 'skills', 'education', 'languages', 'certifications'],
        // Main column holds the name/role header, summary description, projects, and work experience
        main: ['personal-info', 'summary', 'projects', 'experience'],
      },
      overrides: {
        '--font-family-primary': '"Poppins", "Manrope", "Helvetica Neue", Arial, sans-serif',
        '--font-family-secondary': '"Poppins", "Manrope", "Helvetica Neue", Arial, sans-serif',
        '--color-primary': '#4F46E5', // Creative Indigo
        '--color-secondary': '#6366F1',
        '--color-accent': '#A78BFA',
        '--color-text-main': '#1E1B4B',
        '--color-text-light': '#6B7280',
        '--color-border': '#E5E7EB',
        '--color-bg-page': '#FFFFFF',
        '--layout-sidebar-width': '230px',
        '--layout-column-gap': '0px',
        
        // Full bleed styling
        '--spacing-margin': '0px', // Outer page container margin set to zero for full sidebar bleed
        '--layout-sidebar-bg': '#4F46E5', // Solid Indigo sidebar bleed
        '--layout-sidebar-padding': '36px 20px 24px 20px', // Content padding inside the sidebar
        '--layout-sidebar-border': 'none',
        '--layout-sidebar-text': '#FFFFFF', // High-contrast white text inside the Indigo sidebar
        '--layout-main-padding': '36px 32px 24px 28px', // Margin padding inside the main content column
        
        '--spacing-section-mb': '20px',
        '--spacing-item-mb': '16px',
        '--spacing-bullet-gap': '5px',
      },
    },
  },
  styleOverrides: {
    '--font-size-display': '26pt', // Large name text on the right side
    '--weight-display': '800',
    '--heading-weight': '700',
    '--heading-text-transform': 'none',
    '--heading-letter-spacing': '0.02em',
    '--border-thickness-heading': '0',
    '--bullet-style': 'none',
    '--font-size-heading': '10pt',
    '--font-size-body': '9pt',
    '--font-size-bullet': '8.8pt',
    '--line-height-body': '1.6',
  },
};

export default creative_studio;
